import type { FC } from 'react';
import { useContext } from 'react';
import { VolatilityRangeContext } from '../../../../providers/widgets/volatility-range-provider/volatility-range-provider';
import InlineOptions from '../../inline-options';
import Divider from '../../divider';
import {
  TenorEnum,
  MetricEnum,
} from '../../../../providers/widgets/volatility-range-provider/types';
import Chart from '../../chart';
import { useProcessVolatilityRangeData } from './hooks/use-process-volatility-range-data';
import './volatility-range-styles.scss';
import usePersistentState from '../../../../providers/use-persistent-state';
import { TimestampCalendarPicker } from '../../calendar/timestamp-calendar-picker';

interface VolRangeContentProps {
  providerKey: string;
}

const VolatilityRangeContent: FC<VolRangeContentProps> = ({ providerKey }) => {
  const {
    selectedTenor,
    setSelectedTenor,
    selectedMetric,
    setSelectedMetric,
    data,
    isLoading,
    lastTimestamp,
    isLive,
    resetToLive,
    setHistoricalDate,
  } = useContext(VolatilityRangeContext);

  const tenorOptions = Object.values(TenorEnum).map((value) => ({
    value,
    label: value,
  }));

  const metricOptions = Object.values(MetricEnum).map((value) => ({
    value,
    label: value,
  }));

  const { series, xAxis, yAxis } = useProcessVolatilityRangeData(data || []);

  const [legendSelected, setLegendSelected] = usePersistentState<Record<string, boolean>>(
    [providerKey, 'VOL-RANGE'],
    'legendSelected',
    {},
  );

  const handleApply = (newTimestamp: string) => {
    if (!newTimestamp) return;
    setHistoricalDate(newTimestamp);
  };

  const handleReset = () => {
    resetToLive();
  };

  return (
    <div className="volatility-range-content">
      <div className="filters">
        <InlineOptions
          selectedOption={selectedTenor}
          onOptionChange={(option) => setSelectedTenor(option as TenorEnum)}
          options={tenorOptions}
          classname="volatility-range-options"
          leftLabel="Tenor"
        />
        <Divider />
        <InlineOptions
          selectedOption={selectedMetric}
          onOptionChange={(option) => {
            if (Array.isArray(option) && option.length >= 0) {
              setSelectedMetric(option[0] as MetricEnum);

              return;
            }

            setSelectedMetric(option as MetricEnum);
          }}
          options={metricOptions}
          maxVisibleOptions={5}
          classname="volatility-range-options"
        />
      </div>
      <div className="volatility-range-timestamp">
        <TimestampCalendarPicker
          handleChangeDatetime={handleApply}
          isLive={isLive}
          reset={handleReset}
          displayTimestamp={lastTimestamp}
        />
      </div>
      <div className="volatility-range-chart">
        <Chart
          series={series}
          xAxis={xAxis}
          yAxis={yAxis}
          isLoading={isLoading}
          legend={{
            data: ['Current', 'Max', 'Min', 'Range (P25-P75)'],
            selected: legendSelected,
          }}
          onLegendSelectChanged={(payload) => setLegendSelected(payload.selected || {})}
          grid={{ right: 0 }}
          tooltip={{
            trigger: 'item',
            valueFormatter: (value: any) => `${(value * 100).toFixed(2)}%`,
            formatter: (params: any) => {
              if (!params) return '';

              const symbol = params.name || (xAxis.data && xAxis.data[params.dataIndex]);

              if (params.seriesName === 'Range (P25-P75)') {
                const p25 = (params.data[1] * 100).toFixed(2);
                const p75 = (params.data[2] * 100).toFixed(2);
                return `<div style="color: white; font-family: Inter;">
                  <div style="font-weight: 600; margin-bottom: 4px;">${symbol}</div>
                  <div style="color: purple; margin-bottom: 2px;">● Range (P25-P75)</div>
                  <div>P25: ${p25}%</div>
                  <div>P75: ${p75}%</div>
                </div>`;
              }

              const value = Array.isArray(params.data) ? params.data[1] : params.value;
              const percentage = (value * 100).toFixed(2);

              return `<div style="color: white; font-family: Inter;">
                <div style="font-weight: 600; margin-bottom: 4px;">${symbol}</div>
                <div style="color: ${params.color}; margin-bottom: 2px;">● ${params.seriesName}</div>
                <div>${percentage}%</div>
              </div>`;
            },
          }}
        />
      </div>
    </div>
  );
};

export default VolatilityRangeContent;
